import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, Radio, Avatar, IconButton, Divider, Snackbar, Alert, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import { IDeliveryListDTO, IDeliveryPartnersDTO, IReassignOrderRequestDTO } from "../../interfaces/Delivery";
import { getReassignDeliveryPartners, reAssignDelivery } from "../../services/Delivery";

interface ReassignDeliveryPartnerProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  deliveryItem: IDeliveryListDTO | null;
}

const ReassignDeliveryPartner = ({ open, onClose, onSave, deliveryItem }: ReassignDeliveryPartnerProps) => {
  const [selectedPartner, setSelectedPartner] =useState<IDeliveryPartnersDTO | null >(null);
  const [partners, setPartners] = useState<Array<IDeliveryPartnersDTO>>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedPartner(null);
    (async () => {
      try {
        setLoading(true);
        const res = await getReassignDeliveryPartners();
        setPartners(Array.isArray(res) ? res : []);
      } catch {
        setPartners([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const handleAssign = async () => {
    if (!selectedPartner || !deliveryItem) return;
    setSaving(true);
    try {
      if (!deliveryItem || !selectedPartner) return;
      const post = {} as IReassignOrderRequestDTO;
      post.orderId = deliveryItem.orderId;
      post.newPartnerId = selectedPartner.partnerId;
      const res = await reAssignDelivery(post);
      if (res && res.status === "success") {
        setOpenSuccess(true);
        setMsg("Delivery partner reassigned successfully.");
        onSave();
        onClose();
      } else {
        setOpenError(true);
        setMsg("Error reassigning delivery partner.");
      }
    } catch {
      setOpenError(true);
      setMsg("Error reassigning delivery partner.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedPartner(null);
    onClose();
  };

  const handleSnackbarClose = () => {
    setOpenError(false);
    setOpenSuccess(false);
    setMsg("");
  };

  return (
    <>
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", p: 1 } }} slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Reassign Delivery partner</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>Reassign deliveries to a different partner</Typography>
        </Box>
        <IconButton onClick={handleCancel} sx={{ color: "grey.primary" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, maxHeight: "60vh", overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
        <Box sx={{ position: "sticky", top: 0, backgroundColor: "background.paper", pb: 1, zIndex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>Select Delivery Partner</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : partners.length === 0 ? (
          <Typography color="grey.500" sx={{ textAlign: "center", py: 2 }}>No delivery partners available</Typography>
        ) : (
          <Box>
            {partners.map((partner) => (
              <Box
                key={partner.partnerId}
                sx={{
                  border: "1px solid",
                  borderColor: selectedPartner?.partnerId === partner.partnerId ? "primary.main" : "grey.300",
                  borderRadius: "8px",
                  p: 1,
                  mb: 1,
                  backgroundColor: selectedPartner?.partnerId === partner.partnerId ? "primary.light" : "background.paper",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                onClick={() => setSelectedPartner(partner)}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar src={partner.profileImgUrl || undefined} sx={{ mr: 2, backgroundColor: "grey.light" }}>
                    {!partner.profileImgUrl && <PersonIcon sx={{ color: "grey.primary" }} />}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: "primary.main" }}>
                      {partner.totalPackagesAssignedToday} Packages Assigned
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>{partner.partnerName}</Typography>
                  </Box>
                </Box>
                <Radio
                  checked={selectedPartner?.partnerId === partner.partnerId}
                  sx={{ color: selectedPartner?.partnerId === partner.partnerId ? "primary.main" : "grey.light", "&.Mui-checked": { color: "primary.main" } }}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 1, pt: 2, justifyContent: "space-between" }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedPartner || saving}
          sx={{ borderRadius: "6px", border: "1px solid", borderColor: !selectedPartner ? "transparent" : "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none", "&:hover": { backgroundColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset" } }}
        >
          {saving ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Assign partner"}
        </Button>
      </DialogActions>
    </Dialog>

    <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={handleSnackbarClose}>
      <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
    </Snackbar>
    <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openSuccess} autoHideDuration={5000} onClose={handleSnackbarClose}>
      <Alert severity="success" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
    </Snackbar>
    </>
  );
};

export default ReassignDeliveryPartner;
